import { BillingPanel } from '../components/billing/BillingPanel';
import { DocDraftPanel } from '../components/billing/DocDraftPanel';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import { WorkData, workApi } from '../mocks/workApi';
import { ErrorBanner, errorText } from '../components/ErrorBanner';
import { Project } from '../types/project';
import { ConfirmButton } from '../components/ConfirmButton';

/**
 * 산출물 — 무엇을 언제 내야 하는지의 **슬롯**. 파일이 없어도 먼저 선다.
 *
 * 「올리기」는 드라이브 사업 폴더 「04.산출물」에 저장하고 아카이브에 바로 등록한다(2026-10-16).
 * 드라이브에 직접 넣은 파일은 매일 05:00 동기화로 들어오고, 슬롯에서 골라 잇는다. 다음 제안의 재료가 된다.
 */

const DRIVE_HINT = '이 프로젝트에 묶인 아카이브 문서가 없습니다 — 드라이브 사업 폴더에 올리면 매일 05:00 에 들어옵니다';
const dot = (d: string | null) => (d ? d.replace(/-/g, '.').slice(2) : '—');

/** 수행 칸을 뺀 뒤(2026-10-16) 「완료 처리」는 여기서 한다 — 종료로 보낸다 */
export const OutputTab: React.FC<{ project: Project; onComplete: () => Promise<void>; saving: boolean }> = ({ project, onComplete, saving }) => {
  const [data, setData] = useState<WorkData | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ name: '', dueDate: '' });

  const started = project.outcome === '수주';
  const done = project.stage === '종료';

  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    // 끝난 건은 슬롯을 새로 깔지 않는다 — 과거 종료 건을 열기만 해도 빈 슬롯 넷이 생기고 지울 수도 없었다(#110)
    try {
      setData(started && !done ? await workApi.ensureDeliverables(project.id) : await workApi.get(project.id));
    } catch (e) { setError(`산출물을 불러오지 못했습니다 — ${errorText(e)}`); setData({ deliverables: [], documents: [] }); }
  }, [project.id, started, done]);
  useEffect(() => { load(); }, [load]);

  const run = async (work: () => Promise<WorkData>) => {
    setBusy(true);
    setError(null);
    try { setData(await work()); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };

  if (!started) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 py-16 text-center">
        <p className="text-sm text-slate-600">
          {project.outcome === '실주' ? '실주한 프로젝트입니다.' : '아직 산출물 단계가 아닙니다.'}
        </p>
        <p className="mt-1 text-xs text-slate-400">
          {project.outcome === '실주'
            ? '제안서와 견적은 아카이브에 남깁니다 — 다음 제안의 재료입니다.'
            : '수주하면 산출물 슬롯(착수 · 중간 · 최종보고서 · 실적증명)이 깔립니다.'}
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> 불러오는 중...
      </div>
    );
  }

  const items = data.deliverables;
  const submitted = items.filter((d) => d.submittedOn).length;
  const inputCls = 'px-2 py-1 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';

  return (
    <div className="space-y-4">
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {/* 청구 · 거래명세서 — 재무계획 회차마다(작업 10) */}
      {project.outcome === '수주' && <BillingPanel projectId={project.id} projectName={project.name} />}
      {/* 계약서 · 실적증명서 초안(작업 11 · 12) */}
      {project.outcome === '수주' && <DocDraftPanel projectId={project.id} />}
      <section className="bg-white rounded-lg border border-slate-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">산출물</h2>
          <span className="text-xs text-slate-400">{submitted}/{items.length} 제출</span>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-400 border-b border-slate-100">
              <th className="text-left font-medium px-4 py-2">산출물</th>
              <th className="text-left font-medium px-2 py-2 w-24">예정</th>
              <th className="text-left font-medium px-2 py-2 w-24">제출</th>
              <th className="text-left font-medium px-2 py-2 w-28">아카이브</th>
              <th className="text-right font-medium px-2 py-2 w-40">파일</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b border-slate-50">
                <td className="px-4 py-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!d.submittedOn}
                      disabled={done || busy}
                      onChange={(e) => run(() => workApi.setSubmitted(d.id, e.target.checked))}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className={d.submittedOn ? 'text-slate-500 line-through' : 'text-slate-800'}>{d.name}</span>
                  </label>
                </td>
                <td className="px-2 py-2 text-xs text-slate-500 tabular-nums">{dot(d.dueDate)}</td>
                <td className="px-2 py-2 text-xs text-slate-500 tabular-nums">{dot(d.submittedOn)}</td>
                <td className="px-2 py-2 text-xs">
                  {d.archiveDocId ? (
                    <Link to="/archive" title={d.documentTitle ?? undefined} className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                      <FileText size={12} /> <span className="truncate max-w-[7rem]">{d.documentTitle ?? '문서 보기'}</span>
                    </Link>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right whitespace-nowrap">
                  {/* 올리면 드라이브 사업 폴더 「04.산출물」에 저장 · 아카이브 등록 · 이 슬롯에 잇는다(2026-10-16). 드라이브에 이미 있는 문서는 오른쪽에서 고른다 */}
                  {!done && (
                    <label className={`mr-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 ${busy ? 'opacity-40' : 'cursor-pointer hover:bg-slate-50'}`}>
                      <Upload size={12} /> 올리기
                      <input type="file" className="hidden" disabled={busy}
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => workApi.uploadDeliverable(d.id, f)); }} />
                    </label>
                  )}
                  <select
                    value={d.archiveDocId ?? ''}
                    disabled={done || busy || data.documents.length === 0}
                    title={data.documents.length === 0 ? DRIVE_HINT : '이 프로젝트의 아카이브 문서를 잇습니다'}
                    onChange={(e) => run(() => workApi.linkDocument(d.id, e.target.value ? Number(e.target.value) : null))}
                    className="max-w-[10rem] px-2 py-1 rounded-lg text-xs border border-slate-200 text-slate-600 disabled:text-slate-300"
                  >
                    <option value="">{data.documents.length ? '문서 잇기' : '문서 없음'}</option>
                    {data.documents.map((doc) => (
                      <option key={doc.id} value={doc.id}>{doc.title}{doc.docDate ? ` (${doc.docDate.slice(0, 7)})` : ''}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  {!done && !d.archiveDocId && (
                    <button onClick={() => run(() => workApi.removeDeliverable(d.id))} disabled={busy} className="p-1 rounded text-slate-300 hover:text-red-600 hover:bg-red-50">
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {!done && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-slate-100">
            {/* 자주 쓰는 칸 — 드라이브 「04.산출물/칸 이름」 하위 폴더로 나뉜다(작업 9) */}
            {['주간보고', '워크숍', '자문위원회'].filter((n) => !items.some((d) => d.name === n)).map((n) => (
              <button key={n} type="button" onClick={() => setDraft({ ...draft, name: n })} className="px-2 py-1 rounded-full text-xs border border-slate-200 text-slate-600 hover:bg-slate-50">+ {n}</button>
            ))}
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="산출물 이름" className={`${inputCls} flex-1 min-w-[160px]`} />
            <input type="date" value={draft.dueDate} onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })} className={inputCls} />
            <button
              onClick={() => run(() => workApi.addDeliverable(project.id, draft.name.trim(), draft.dueDate || null)).then(() => setDraft({ name: '', dueDate: '' }))}
              disabled={busy || !draft.name.trim()}
              className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-40"
            >
              <Plus size={12} /> 슬롯 추가
            </button>
          </div>
        )}
      </section>

      <div className="flex items-center justify-end gap-2">
        {done ? (
          <span className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">완료</span>
        ) : (
          <ConfirmButton onConfirm={onComplete} disabled={saving || busy} ask="종료로 보냅니다"
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
            완료 처리
          </ConfirmButton>
        )}
      </div>
    </div>
  );
};
